/**
 * OAuth 2.0 Device Authorization Grant (RFC 8628) for Auth0.
 *
 * Enables headless CLI login: the user authorizes in a browser while the CLI polls for a token.
 */

import { createArbiClient } from '@arbidocs/client'

// ── Types ────────────────────────────────────────────────────────────────────

export interface SsoConfig {
  ssoEnabled: boolean
  domain: string
  clientId: string
  audience: string
}

export interface DeviceCodeResponse {
  device_code: string
  user_code: string
  verification_uri: string
  verification_uri_complete: string
  expires_in: number
  interval: number
}

// ── Errors ───────────────────────────────────────────────────────────────────

export class DeviceFlowError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DeviceFlowError'
  }
}

export class DeviceFlowExpired extends DeviceFlowError {
  constructor() {
    super('Device code expired — please try again')
    this.name = 'DeviceFlowExpired'
  }
}

export class DeviceFlowAccessDenied extends DeviceFlowError {
  constructor() {
    super('Authorization was denied by the user')
    this.name = 'DeviceFlowAccessDenied'
  }
}

// ── Functions ────────────────────────────────────────────────────────────────

/**
 * Fetch SSO configuration from the ARBI deployment.
 */
export async function fetchSsoConfig(baseUrl: string): Promise<SsoConfig> {
  const arbi = createArbiClient({ baseUrl, deploymentDomain: '', credentials: 'omit' })
  const { data, error } = await arbi.fetch.GET('/v1/user/sso-config')
  if (error || !data) {
    throw new DeviceFlowError(`Failed to fetch SSO config`)
  }
  return {
    ssoEnabled: data.sso_enabled,
    domain: data.domain,
    clientId: data.cli_client_id || data.client_id,
    audience: data.audience,
  }
}

/**
 * Request a device code from Auth0.
 *
 * POST https://{domain}/oauth/device/code
 */
export async function requestDeviceCode(
  domain: string,
  clientId: string,
  audience?: string,
  scope: string = 'openid email profile'
): Promise<DeviceCodeResponse> {
  const body = new URLSearchParams({
    client_id: clientId,
    scope,
    ...(audience ? { audience } : {}),
  })

  const res = await fetch(`https://${domain}/oauth/device/code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })

  if (!res.ok) {
    const text = await res.text()
    throw new DeviceFlowError(`Device code request failed: ${res.status} ${text}`)
  }

  return (await res.json()) as DeviceCodeResponse
}

/**
 * Poll Auth0 token endpoint until the user authorizes (or the code expires).
 *
 * Returns the Auth0 access_token (JWT).
 */
export async function pollForToken(
  domain: string,
  clientId: string,
  deviceCode: string,
  interval: number,
  expiresIn: number,
  onPoll?: (elapsedMs: number) => void
): Promise<string> {
  const deadline = Date.now() + expiresIn * 1000
  let pollInterval = interval * 1000 // seconds → ms

  while (Date.now() < deadline) {
    await sleep(pollInterval)
    onPoll?.(Date.now())

    const res = await fetch(`https://${domain}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
        client_id: clientId,
        device_code: deviceCode,
      }),
    })

    if (res.ok) {
      const data = (await res.json()) as { access_token: string }
      return data.access_token
    }

    const errBody = (await res.json().catch(() => ({ error: 'unknown' }))) as {
      error: string
      error_description?: string
    }

    if (errBody.error === 'authorization_pending') {
      continue
    } else if (errBody.error === 'slow_down') {
      pollInterval += 5000
      continue
    } else if (errBody.error === 'expired_token') {
      throw new DeviceFlowExpired()
    } else if (errBody.error === 'access_denied') {
      throw new DeviceFlowAccessDenied()
    } else {
      throw new DeviceFlowError(
        `Token polling error: ${errBody.error} — ${errBody.error_description ?? ''}`
      )
    }
  }

  throw new DeviceFlowExpired()
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
